import {
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RunnitNodeRunResultType,
} from '@run-diffusion/shared';

export const LIBRARY_BULK_RESULT_TYPES: RunnitNodeRunResultType[] = [
	RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
	RUNNIT_NODE_RUN_RESULT_TYPE.VID,
	RUNNIT_NODE_RUN_RESULT_TYPE.LAYERS,
	RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D,
];

export interface LibrarySelectedNodeRun {
	nodeRun: RunnitNodeRun;
	results: RunnitNodeRunResult[];
}

/**
 * How many results one "Select all filtered" may commit.
 *
 * WHY A CEILING AT ALL. Select All drains the filtered set page by page, and
 * the set is unbounded: a busy team Library is tens of thousands of results.
 * Without a ceiling one click can commit the browser to hundreds of sequential
 * requests, inflate the grid's store to the whole set, and then hand thousands
 * of items to bulk actions written for tens. The two payoff actions are the
 * real constraint, not the drain:
 *
 *   Download zips client side. Per result it makes TWO sequential round trips
 *   (a Storage getDownloadURL, then the fetch of the full-size original), holds
 *   every file as base64 (1.37x its bytes), and then builds the whole archive
 *   in memory. Peak is roughly 2.4x the total original bytes, all resident.
 *
 *   Delete does one Firestore read plus one write per node run, sequentially,
 *   and commits Vuex state per run.
 *
 * WHY 500. The number is chosen to sit in the gap between "nobody reaches it"
 * and "waiting for it feels broken". Above it, nothing good happens: reaching
 * 500 results takes roughly 5 to 21 requests (a page is 24 RUNS, each carrying
 * one or more results), so a few seconds, while ten times that is minutes of a
 * dead toolbar. Below it, we would start refusing ordinary work: filtered views
 * that real people select in bulk (a shoot, a week, a board, a favourites set)
 * land in the tens and low hundreds, so 500 leaves several times the headroom
 * anyone is expected to use. The intent is that this ceiling is never met in
 * normal use and only ever catches a filter that barely narrowed anything.
 *
 * WHAT IT DOES NOT SOLVE. 500 is safe for the drain and tolerable for delete,
 * but it is not a safe download budget: 500 images at 1.5 MB is about 1.8 GB
 * peak, and 500 videos is not close. The download path needs its own budget in
 * BYTES, because a count cannot tell those two cases apart. Tracked separately.
 *
 * Truncation is never silent: a capped Select All reports it so the caller can
 * tell the user to narrow the filter.
 */
export const LIBRARY_SELECT_ALL_MAX_RESULTS = 500;

export interface LibrarySelectionSnapshot {
	selectedNodeRunsMap: Record<string, LibrarySelectedNodeRun>;
	selectedNodeRunResults: RunnitNodeRunResult[];
	/** True when eligible loaded results were left out to respect the ceiling. */
	truncated: boolean;
}

/**
 * `maxResults` stops at a RUN boundary rather than mid-run: once the ceiling is
 * reached the walk finishes the run it is inside and refuses the next one, so
 * the selection can overshoot slightly. That keeps delete's per-run batching
 * whole and avoids a row where some tiles of one generation are checked and
 * others are not for no reason the user can see.
 */
export function buildLibrarySelectionSnapshot(
	visibleResults: RunnitNodeRunResult[],
	nodeRunsById: Record<string, RunnitNodeRun>,
	canSelectTypes: string[],
	maxResults?: number,
): LibrarySelectionSnapshot {
	const selectedNodeRunsMap: Record<string, LibrarySelectedNodeRun> = {};
	const selectedNodeRunResults: RunnitNodeRunResult[] = [];
	const seen = new Set<string>();
	let truncated = false;

	visibleResults.forEach((result) => {
		const nodeRunId = result?.nodeRunId;
		const nodeRun = nodeRunId ? nodeRunsById[nodeRunId] : null;
		const isStoredResult = !!nodeRun?.results?.some((candidate) => candidate.uuid === result.uuid);
		if (
			!nodeRun ||
			!isStoredResult ||
			!result.uuid ||
			seen.has(result.uuid) ||
			result.deletedAt ||
			!canSelectTypes.includes(result.type)
		) return;

		if (
			maxResults !== undefined &&
			selectedNodeRunResults.length >= maxResults &&
			!selectedNodeRunsMap[nodeRunId]
		) {
			truncated = true;
			return;
		}

		seen.add(result.uuid);
		selectedNodeRunResults.push(result);
		if (!selectedNodeRunsMap[nodeRunId]) {
			selectedNodeRunsMap[nodeRunId] = { nodeRun, results: [] };
		}
		selectedNodeRunsMap[nodeRunId].results.push(result);
	});

	return { selectedNodeRunsMap, selectedNodeRunResults, truncated };
}

/**
 * Mirror of the authoritative Firestore rule for soft-deleting a run
 * (firestore.rules, `match /runnitNodeRuns/{id}` -> allow update):
 *
 *   isUid(resource.data.userId)
 *   || (resource.data.teamId != null
 *       && hasTeamPermission(database, resource.data.teamId, 'MANAGE_ALL_GENERATIONS'))
 *
 * with `hasTeamPermission` ending in `|| isUidAdminUser(database)`.
 *
 * Two details are load bearing and easy to get wrong. The permission is per the
 * RUN's team, not the team the caller happens to be acting as. And the admin
 * override lives INSIDE the `teamId != null` branch, so a system admin can
 * delete another member's team generation but NOT another user's personal one;
 * `isSystemAdmin` is placed to match, rather than short-circuiting the whole
 * check.
 *
 * This is a UI affordance, not the boundary. Firestore refuses regardless, so
 * being wrong here means either offering a delete that then fails, or hiding
 * one that would have worked. The second is what users notice.
 */
export function canDeleteLibraryNodeRuns(
	nodeRuns: RunnitNodeRun[],
	callerUserId: string,
	manageableTeamIds: string[],
	isSystemAdmin: boolean = false,
): boolean {
	if (!nodeRuns.length) return false;
	const manageable = new Set(manageableTeamIds);
	return nodeRuns.every((nodeRun) => (
		nodeRun.userId === callerUserId ||
		(!!nodeRun.teamId && (manageable.has(nodeRun.teamId) || isSystemAdmin))
	));
}

interface DrainLibrarySelectionPagesInput {
	hasMore: () => boolean;
	cursor: () => unknown;
	loadNextPage: () => Promise<'success' | 'failed' | 'skipped'>;
	isStale: () => boolean;
	/** Results loaded so far, against which `maxResults` is measured. */
	loadedCount: () => number;
	/** Stop paging once this many results are loaded. See LIBRARY_SELECT_ALL_MAX_RESULTS. */
	maxResults: number;
}

export type DrainLibrarySelectionPagesResult =
	| { status: 'success'; truncated: boolean }
	| { status: 'failed' | 'stale' };

/**
 * Drain the renderer's own deduplicated page loader, up to `maxResults`.
 * Selection stays unpublished until this reports success, while every
 * successful page becomes part of the same store ordinary scrolling renders.
 *
 * The ceiling is checked BEFORE each page so a capped run never fetches a page
 * it will not use. Stopping with pages left is reported as `truncated`, not as
 * a failure: the selection it produces is complete and actionable, it just is
 * not everything the filter matches.
 */
export async function drainLibrarySelectionPages(
	input: DrainLibrarySelectionPagesInput,
): Promise<DrainLibrarySelectionPagesResult> {
	while (input.hasMore()) {
		if (input.loadedCount() >= input.maxResults) return { status: 'success', truncated: true };
		if (input.isStale()) return { status: 'stale' };
		const cursor = input.cursor();
		const pageStatus = await input.loadNextPage();
		if (input.isStale()) return { status: 'stale' };
		if (pageStatus === 'failed') return { status: 'failed' };
		if (input.hasMore() && input.cursor() === cursor) return { status: 'failed' };
	}

	return { status: 'success', truncated: false };
}
